<script lang="ts" setup>
import type { FederatedPointerEvent } from 'pixi.js'
import type { ContentSvg } from '~/models/Content/ContentSvg.js'
import type { ContentSceneObject } from '~/models/Content/ContentSceneObject'
import type { ContentScene } from '~/models/Content/ContentScene'
import type { ContentImage } from '~/models/Content/ContentImage.js'
import { computed, onUnmounted, reactive, ref, shallowRef, useTemplateRef, watch } from 'vue'
import { Application, Assets, Container, Graphics, Sprite } from 'pixi.js'
import { storeToRefs } from 'pinia'
import { useQuery } from '@tanstack/vue-query'
import { faAngle, faArrowPointer, faClone, faInfo, faPlusLarge, faQuestion, faRulerHorizontal, faTrash, faTrashXmark } from '@fortawesome/pro-regular-svg-icons'
import { KsButton, KsDialog, KsIcon, KsSwitch } from '@aschehoug/kloss'
import { setTitle } from '~/utils/dom.js'
import { colorMap } from '~/utils/colors'
import useLicenseControlStore from '~/stores/licenseControl.js'
import useAuthStore from '~/stores/auth.js'
import { PendoFeature, PendoTrackName } from '~/models/Pendo.js'
import { Subtree } from '~/models/Content/Subtree'
import { ContentType } from '~/models/Content/ContentType'
import { ColorName } from '~/models/Content/Color'
import { useUrlBuilder } from '~/composables/useUrlBuilder'
import usePendo from '~/composables/usePendo.js'
import useContentApi from '~/api/contentApi'
import TopBar from '~/components/utils/TopBar.vue'
import RichTextRenderer from '~/components/utils/RichTextRenderer.vue'
import Image from '~/components/media/Image.vue'
import ArticleTeacherGuide from '~/components/article/ArticleTeacherGuide.vue'

const { locationId } = defineProps<{
  locationId: number
}>()

const width = 1024
const height = 768
const Label = {
  Shape: 'Shape',
  Outline: 'Outline',
  RotateHandle: 'Rotate handle',
  ResizeHandle: 'Resize handle',
}

const canvas = useTemplateRef('canvas')
const app = new Application()
const activeContainer = shallowRef<Container>()
const activeContent = computed(() => activeContainer.value ? inUse.get(activeContainer.value) : undefined)
const dragging = ref(false)
const lastActiveX = ref(0)
const lastActiveY = ref(0)
const lastActiveW = ref(0)
const lastActiveA = ref(0)
const pointerX = ref(0)
const pointerY = ref(0)
const ready = ref(false)

const { checkItemLicense } = useLicenseControlStore()
const { buildSvgUrlByField } = useUrlBuilder()
const { findContents } = useContentApi()
const { pendoTrack, pendoFlushNow } = usePendo()
const { isTeacher } = storeToRefs(useAuthStore())

const scene = useQuery({
  queryKey: ['scene', locationId],
  staleTime: Infinity,
  async queryFn() {
    const [location] = await findContents<ContentScene>({
      contentTypeCriterion: [ContentType.Scene],
      subtreeCriterion: [Subtree.Content, Subtree.Media],
      locationIdCriterion: [locationId]
    })

    setTitle(location.title)
    checkItemLicense(location)

    if (location.backgroundColor) {
      app.renderer.background.color = location.backgroundColor
    }

    return location
  }
})

const sceneObjects = useQuery({
  queryKey: ['scene-objects', locationId],
  initialData: { locations: [], images: [] },
  initialDataUpdatedAt: 0,
  staleTime: 1000 * 60 * 60 * 12,
  enabled: computed(() => scene.data.value != null),
  async queryFn() {
    if (!scene.data.value)
      return { locations: [], images: [] }

    const locations = (await findContents<ContentSceneObject>({
      contentTypeCriterion: [ContentType.SceneObject],
      subtreeCriterion: [Subtree.Content, Subtree.Media],
      contentIdCriterion: [...new Set([
        ...scene.data.value.availableObjects.destinationContentIds,
        ...scene.data.value.placedObjects.destinationContentIds,
      ])]
    })).filter(({ image }) => image.destinationContentId)

    const images = await findContents<ContentImage | ContentSvg>({
      contentTypeCriterion: [ContentType.Image, ContentType.Svg],
      contentIdCriterion: locations.map(({ image }) => Number(image.destinationContentId)),
      mainLocationCriterion: true,
      subtreeCriterion: [Subtree.Content, Subtree.Media],
    })

    return { locations, images }
  },
})

const availableObjects = computed(() =>
  scene
    .data
    .value
    ?.availableObjects
    .destinationContentIds
    .map(contentId => sceneObjects.data.value.locations.find(location => location.contentId === contentId))
    .filter(obj => obj !== undefined)
    ?? []
)

const placedObjects = computed(() =>
  scene
    .data
    .value
    ?.placedObjects
    .destinationContentIds
    .map(contentId => sceneObjects.data.value.locations.find(location => location.contentId === contentId))
    .filter(obj => obj !== undefined)
    .reverse()
    ?? []
)

const inUse = reactive(new Map<Container, ContentSceneObject>())
const isGuideOpen = ref(false)
const isTeacherGuideOpen = ref(false)
const isMeasurementsVisible = ref(false)
const isResetSceneOpen = ref(false)

function resetScene() {
  [...inUse.keys()].forEach(obj => obj.destroy())
  inUse.clear()
  placedObjects.value.forEach(location => createObject(location))
  isResetSceneOpen.value = false
}

app
  .init({
    backgroundColor: scene.data.value?.backgroundColor ?? colorMap.get(ColorName.Seagreen60)?.rgb,
    width,
    height,
    resolution: 1,
    autoDensity: true,
    antialias: true,
    roundPixels: true,
    eventMode: 'static',
  })
  .then(() => ready.value = true)

function setSize(container: Container, width: number, max: number = width) {
  const shape = container.getChildByLabel(Label.Shape)
  const outline = container.getChildByLabel(Label.Outline)
  const rotateHandle = container.getChildByLabel(Label.RotateHandle)
  const resizeHandle = container.getChildByLabel(Label.ResizeHandle)

  if (!shape)
    return

  const w = Math.max(Math.min(width, max), 32)
  const h = w * (shape.height / shape.width)
  container.pivot = { x: w / 2, y: h / 2 }
  shape.setSize(w, h)

  if (outline) {
    outline.setSize(w, h)
  }

  if (rotateHandle) {
    rotateHandle.x = w / 2
    rotateHandle.y = -24
  }

  if (resizeHandle) {
    resizeHandle.x = w - 10
    resizeHandle.y = h - 10
  }

  if (activeContainer.value === container) {
    lastActiveW.value = w
  }
}

function createImageUrl(image: ContentImage | ContentSvg) {
  switch (image.contentTypeIdentifier) {
    case ContentType.Svg:
      return buildSvgUrlByField(image.contentId)

    case ContentType.Image:
    default:
      return (image as ContentImage).image.uri
  }
}

interface CreateObjectSettings {
  x?: number
  y?: number
  angle?: number
  width?: number
  dragging?: boolean
}

async function createObject(content: ContentSceneObject, settings?: CreateObjectSettings): Promise<Container | undefined> {
  if (content.maxAmount && getAmountLeft(content) < 1)
    return

  const imageLocation = sceneObjects.data.value.images.find(image => image.contentId === content.image.destinationContentId)
  let image: Graphics | Sprite

  if (!imageLocation)
    return

  const container = new Container()
  app.stage.addChild(container)

  switch (imageLocation.contentTypeIdentifier) {
    case ContentType.Svg:
      image = new Graphics(await Assets.load({
        src: createImageUrl(imageLocation),
        data: {
          parseAsGraphicsContext: true,
        },
      }))
      break

    case ContentType.Image:
    default:
      image = new Sprite(await Assets.load({
        src: createImageUrl(imageLocation),
      }))
      break
  }

  const outline = new Graphics()
    .rect(0, 0, image.width, image.height)
    .stroke({ color: colorMap.get(ColorName.Blue30)?.rgb, pixelLine: true })

  const rotateHandle = new Graphics()
    .circle(0, 0, 10)
    .fill('white')
    .stroke({ color: 'black', pixelLine: true })

  const resizeHandle = new Graphics()
    .rect(0, 0, 20, 20)
    .fill('white')
    .stroke({ color: 'black', pixelLine: true })

  container.label = content.title
  image.label = Label.Shape
  outline.label = Label.Outline
  rotateHandle.label = Label.RotateHandle
  resizeHandle.label = Label.ResizeHandle

  inUse.set(container, content)

  container.angle = settings?.angle ?? content.angle ?? 0
  container.x = settings?.x ?? content.x ?? app.screen.width / 2
  container.y = settings?.y ?? content.y ?? app.screen.height / 2
  container.addChild(image)

  if (!content.static) {
    const state = ref<'idle' | 'hover' | 'active'>('idle')

    container.accessible = true

    if (content) {
      container.accessibleTitle = content.title
    }

    resizeHandle.cursor = 'nwse-resize'
    rotateHandle.cursor = 'nwse-resize'

    container.addChild(outline)
    if (content.rotatable)
      container.addChild(rotateHandle)
    if (content.resizable)
      container.addChild(resizeHandle)

    let initMoveOffset: { x: number, y: number }

    const onMoveStart = (event: FederatedPointerEvent) => {
      event.stopPropagation()
      initMoveOffset = { x: event.global.x - container.x, y: event.global.y - container.y }
      app.stage.on('globalpointermove', onMove)
      app.stage.on('pointerup', onMoveEnd)
      app.stage.on('pointerupoutside', onMoveEnd)
    }
    const onMove = (event: FederatedPointerEvent) => {
      if (!initMoveOffset)
        return

      container.parent.toLocal({ x: event.global.x - initMoveOffset.x, y: event.global.y - initMoveOffset.y }, undefined, container.position)
      lastActiveX.value = container.x
      lastActiveY.value = container.y
      dragging.value = true
    }
    const onMoveEnd = (event: FederatedPointerEvent) => {
      dragging.value = false
      app.stage.off('globalpointermove', onMove)
      app.stage.off('pointerup', onMoveEnd)
      app.stage.off('pointerupoutside', onMoveEnd)

      if (container.x < 0)
        container.x = 0
      if (container.x > width)
        container.x = width
      if (container.y < 0)
        container.y = 0
      if (container.y > height)
        container.y = height
    }

    const onHoverStart = () => {
      if (state.value === 'idle') {
        state.value = 'hover'
        image.on('pointerleave', onHoverEnd)
      }
    }

    const onHoverEnd = () => {
      if (state.value === 'hover') {
        state.value = 'idle'
      }

      image.off('pointerleave', onHoverEnd)
    }

    let initResizeEvent: { x: number, y: number, w: number, h: number }

    const onResizeStart = (event: FederatedPointerEvent) => {
      event.stopPropagation()
      initResizeEvent = { x: event.screenX, y: event.screenY, h: image.height, w: image.width }
      app.stage.on('globalpointermove', onResizeMove)
      app.stage.on('pointerup', onResizeEnd)
      app.stage.on('pointerupoutside', onResizeEnd)
    }
    const onResizeMove = (event: FederatedPointerEvent) => {
      if (!initResizeEvent)
        return

      const r = container.rotation
      const { w, h } = initResizeEvent
      const vecX = event.screenX - initResizeEvent.x
      const vecY = event.screenY - initResizeEvent.y
      const x = vecX * Math.cos(r) + vecY * Math.sin(r)
      const y = -vecX * Math.sin(r) + vecY * Math.cos(r)
      const distance = Math.max(x, y * (w / h))

      setSize(
        container,
        w + (2 * distance),
        app.screen.width * 0.75
      )

      lastActiveW.value = image.width
    }
    const onResizeEnd = () => {
      app.stage.off('globalpointermove', onResizeMove)
      app.stage.off('pointerup', onResizeEnd)
      app.stage.off('pointerupoutside', onResizeEnd)
    }

    const onRotateStart = (event: FederatedPointerEvent) => {
      event.stopPropagation()
      app.stage.on('globalpointermove', onRotate)
      app.stage.on('pointerup', onRotateEnd)
      app.stage.on('pointerupoutside', onRotateEnd)
    }

    const onRotate = (event: FederatedPointerEvent) => {
      container.rotation = Math.atan2(
        container.y - event.screenY,
        container.x - event.screenX
      ) - (Math.PI / 2)

      lastActiveA.value = container.angle
    }

    const onRotateEnd = () => {
      app.stage.off('globalpointermove', onRotate)
      app.stage.off('pointerup', onRotateEnd)
      app.stage.off('pointerupoutside', onRotateEnd)
    }

    watch(activeContainer, (newContainer, oldContainer) => {
      if (newContainer === container) {
        state.value = 'active'
      }

      if (oldContainer === container) {
        state.value = 'idle'
      }
    })

    watch(state, (value) => {
      container.zIndex = 0
      outline.visible = false
      resizeHandle.visible = false
      rotateHandle.visible = false

      switch (value) {
        case 'hover': {
          outline.visible = true
          break
        }

        case 'active': {
          container.zIndex = 1
          outline.visible = true
          resizeHandle.visible = true
          rotateHandle.visible = true
          lastActiveA.value = container.angle
          lastActiveX.value = container.x
          lastActiveY.value = container.y
          lastActiveW.value = image.width
          break
        }
      }
    }, { immediate: true })

    container.eventMode = 'static'

    if (content.movable) {
      image.on('pointerdown', onMoveStart)
    }

    if (content.resizable) {
      resizeHandle.on('pointerdown', onResizeStart)
    }

    if (content.rotatable) {
      rotateHandle.on('pointerdown', onRotateStart)
    }

    const onActive = () => {
      activeContainer.value = container
    }

    image.on('pointerenter', onHoverStart)
    image.on('pointerdown', onActive)
    container.on('destroyed', () => {
      inUse.delete(container)
      if (activeContainer.value === container)
        activeContainer.value = undefined
    })

    if (settings?.dragging) {
      app.stage.once('globalpointermove', (event) => {
        container.x = event.global.x
        container.y = event.global.y
        onMoveStart(event)
      })
      activeContainer.value = container
    }
  }

  setSize(container, settings?.width ?? content.width)
  return container
}

function onKeyDown(event: KeyboardEvent) {
  if (activeContainer.value) {
    const content = inUse.get(activeContainer.value)
    let prevent = true

    if (!content || content.static)
      return

    switch (event.key) {
      case 'ArrowLeft':
        if (content.movable)
          activeContainer.value.x -= 10
        break
      case 'ArrowRight':
        if (content.movable)
          activeContainer.value.x += 10
        break
      case 'ArrowUp':
        if (content.movable)
          activeContainer.value.y -= 10
        break
      case 'ArrowDown':
        if (content.movable)
          activeContainer.value.y += 10
        break
      case 'j':
        if (content.rotatable)
          activeContainer.value.angle -= 10
        break
      case 'k':
        if (content.rotatable)
          activeContainer.value.angle += 10
        break
      case '+':
        if (content.resizable)
          setSize(activeContainer.value, lastActiveW.value + 10)
        break
      case '-':
        if (content.resizable)
          setSize(activeContainer.value, lastActiveW.value - 10)
        break
      case 'Delete':
        activeContainer.value.destroy()
        break
      default:
        prevent = false
    }

    if (prevent)
      event.preventDefault()
  }
}

async function clone(container: Container) {
  const content = inUse.get(container)

  if (!content)
    return

  const clonedContainer = await createObject(content, {
    x: lastActiveX.value + 16,
    y: lastActiveY.value + 16,
    width: lastActiveW.value,
    angle: lastActiveA.value,
  })

  if (clonedContainer)
    activeContainer.value = clonedContainer
}

function onFocusIn(event: FocusEvent) {
  // NOTE: pixi adds .container to its accessibility DOM elements
  if (event.target && 'container' in event.target && inUse.has(event.target.container as Container)) {
    activeContainer.value = event.target.container as Container
  }
}

function onFocusOut(event: FocusEvent) {
  if (event.relatedTarget instanceof Node && !canvas.value?.contains(event.relatedTarget)) {
    activeContainer.value = undefined
  }
}

function onDragCreate(content: ContentSceneObject) {
  createObject(content, { dragging: true, x: pointerX.value, y: pointerY.value })
  pendoTrack(PendoTrackName.SceneDragAction, {
    title: scene.data.value?.title ?? document.title,
    url: window.location.href,
    action: 'create',
    contentId: content.contentId,
  })
  pendoFlushNow()
}

function onDragDelete(container: Container) {
  container.destroy()
  pendoTrack(PendoTrackName.SceneDragAction, {
    title: scene.data.value?.title ?? document.title,
    url: window.location.href,
    action: 'delete',
  })
  pendoFlushNow()
}

function getAmountLeft(content: ContentSceneObject) {
  if (!content.maxAmount)
    return 0

  return content.maxAmount - [...inUse.values()].reduce((n, { contentId }) => contentId === content.contentId ? n + 1 : n, 0)
}

watch([placedObjects, ready], ([objs, ready]) => {
  if (!ready)
    return

  objs.forEach(location => createObject(location))
}, { immediate: true })

watch([canvas, ready], async ([el, ready]) => {
  if (!el || !ready) {
    return
  }

  el.appendChild(app.canvas)
  app.stage.hitArea = app.screen
  app.renderer.resolution = (el.clientWidth * devicePixelRatio) / width

  const observer = new ResizeObserver(() => {
    app.renderer.resolution = (el.clientWidth * devicePixelRatio) / width
    app.stage.hitArea = app.screen
  })

  observer.observe(app.canvas)

  app.stage.on('pointerdown', () => {
    activeContainer.value = undefined
  })

  app.stage.on('globalpointermove', (event) => {
    pointerX.value = event.global.x
    pointerY.value = event.global.y
  })
})

onUnmounted(() => {
  app.destroy()
  sceneObjects.data.value.images.forEach(image => Assets.unload(createImageUrl(image)))
})
</script>

<template>
  <div class="h-screen w-screen touch-none content-center bg-seagreen-60 p-4">
    <TopBar :item="scene.data.value" />
    <div
      ref="canvas"
      class="m-auto grid aspect-[4/3] max-h-full grid-cols-1 grid-rows-1 [&:focus-within>canvas]:ring [&>canvas]:!h-full [&>canvas]:!w-full [&>canvas]:rounded-xl [&>canvas]:border [&>canvas]:border-white/20"
      @keydown="onKeyDown"
      @focusin="onFocusIn"
      @focusout="onFocusOut"
    />

    <div
      v-if="activeContainer"
      class="absolute left-1/2 flex -translate-x-1/2 gap-3 rounded-full bg-green-50 p-2 text-white portrait:top-20 landscape:top-3"
      :class="{ 'min-w-48 !bg-coral-40 hover:!bg-coral-pop': dragging }"
    >
      <KsButton
        v-if="activeContent?.maxAmount === undefined || getAmountLeft(activeContent) > 0"
        variant="secondary"
        :icon-left="faClone"
        :aria-label="$t('scene.clone')"
        shape="round"
        class="!transition-none"
        :class="{ invisible: dragging }"
        :data-pendo="PendoFeature.Scene.Clone"
        @click="clone(activeContainer)"
      />

      <KsButton
        variant="secondary"
        shape="round"
        :icon-left="faTrash"
        :aria-label="$t('scene.delete')"
        class="!transition-none"
        :class="{ invisible: dragging }"
        :data-pendo="PendoFeature.Scene.ClickDelete"
        @click="activeContainer.destroy()"
      />

      <span
        v-show="dragging"
        class="absolute inset-0 flex select-none items-center justify-center gap-2"
        @pointerup="onDragDelete(activeContainer)"
      >
        <KsIcon :icon="faTrash" />
        <span v-text="$t('scene.dragDelete')" />
      </span>
    </div>

    <div class="absolute grid w-max auto-cols-fr auto-rows-fr gap-2 overflow-hidden rounded-full bg-green-50 p-2 text-white portrait:left-1/2 portrait:top-2 portrait:max-w-full portrait:-translate-x-1/2 portrait:grid-flow-col landscape:left-2 landscape:top-1/2 landscape:max-h-[80lvh] landscape:-translate-y-1/2">
      <KsButton
        variant="secondary"
        :icon-left="faTrashXmark"
        :aria-label="$t('scene.reset')"
        shape="round"
        @click="isResetSceneOpen = true"
      />

      <Teleport to=".app-top">
        <KsDialog
          :title="$t('scene.reset')"
          :open="isResetSceneOpen"
          @close="isResetSceneOpen = false"
        >
          <template #body>
            <p v-text="$t('scene.resetText')" />
          </template>

          <template #footer>
            <KsButton
              variant="primary"
              :data-pendo="PendoFeature.Scene.Reset"
              @click="resetScene"
            >
              {{ $t('scene.resetConfirm') }}
            </KsButton>
          </template>
        </KsDialog>
      </Teleport>

      <KsButton
        variant="secondary"
        :icon-left="faQuestion"
        :aria-label="$t('help')"
        :data-pendo="PendoFeature.Scene.Help"
        shape="round"
        @click="isGuideOpen = !isGuideOpen"
      />

      <Teleport to=".app-top">
        <KsDialog
          :title="$t('help')"
          :open="isGuideOpen"
          @close="isGuideOpen = false"
        >
          <template #body>
            <article class="prose prose-au mx-auto prose-img:rounded-xl">
              <RichTextRenderer v-if="scene.data.value?.studentGuide" :text="scene.data.value.studentGuide" />

              <h2 v-text="$t('scene.keyboard')" />

              <ul>
                <li v-text="$t('scene.keyboardMove')" />
                <li v-text="$t('scene.keyboardResize')" />
                <li v-text="$t('scene.keyboardRotate')" />
              </ul>

              <h2 v-text="$t('scene.settings')" />

              <p>
                <label class="flex items-center">
                  <KsSwitch v-model="isMeasurementsVisible" />
                  {{ $t('scene.showMeasurements') }}
                </label>
              </p>
            </article>
          </template>
        </KsDialog>
      </Teleport>

      <template
        v-if="isTeacher && scene.data.value?.teacherContent?.destinationContentId"
      >
        <KsButton
          variant="secondary"
          :icon-left="faInfo"
          :aria-label="$t('presentation.teacherGuide')"
          shape="round"
          @click="isTeacherGuideOpen = !isTeacherGuideOpen"
        />

        <ArticleTeacherGuide
          :is-open="isTeacherGuideOpen"
          :content-id="scene.data.value.teacherContent.destinationContentId"
          @close-guide="isTeacherGuideOpen = false"
        />
      </template>
    </div>

    <div class="absolute grid w-max auto-cols-fr auto-rows-fr gap-2 overflow-hidden rounded-xl bg-green-50 p-2 text-white portrait:bottom-2 portrait:left-1/2 portrait:max-w-full portrait:-translate-x-1/2 portrait:grid-flow-col landscape:right-2 landscape:top-1/2 landscape:max-h-[80lvh] landscape:-translate-y-1/2">
      <button
        v-for="obj in availableObjects"
        :key="obj.locationId"
        class="relative grid max-w-32 touch-none grid-cols-1 grid-rows-[minmax(0,1fr),max-content] gap-2 focus-visible:ring"
        draggable="true"
        :data-pendo="PendoFeature.Scene.ClickCreate"
        @click="createObject(obj)"
        @dragstart.prevent="onDragCreate(obj)"
      >
        <div class="pointer-events-none grid max-h-full min-h-0 min-w-0 max-w-full grid-cols-1 grid-rows-1 place-content-center overflow-hidden rounded-lg border border-green-10 p-2">
          <Image
            :content="Number(obj.image.destinationContentId)"
            :style="{ rotate: `${obj.angle}deg` }"
            width="128px"
            class="aspect-square !h-full min-h-0 !w-full min-w-0"
          />
        </div>

        <span
          v-if="obj.maxAmount !== undefined"
          class="pointer-events-none absolute right-2 top-2 rounded-full bg-green-10 px-1 text-black before:content-[attr(data-amount)] [&[data-amount='0']]:opacity-50"
          :data-amount="getAmountLeft(obj)"
        />

        <span class="pointer-events-none">{{ obj.title }}</span>
      </button>
    </div>

    <div v-if="isMeasurementsVisible" class="absolute bottom-0 left-0 flex items-center gap-2 self-center bg-seagreen-60/90 p-2 tabular-nums text-white">
      <KsIcon :icon="faArrowPointer" />
      <span><span class="text-white/50">X</span>{{ Math.round(pointerX) }}, <span class="text-white/50">Y</span>{{ Math.round(pointerY) }}</span>
      <template v-if="activeContainer">
        <KsIcon :icon="faPlusLarge" />
        <span><span class="text-white/50">X</span>{{ Math.round(lastActiveX) }}, <span class="text-white/50">Y</span>{{ Math.round(lastActiveY) }}</span>
        <KsIcon :icon="faRulerHorizontal" />
        {{ Math.round(lastActiveW) }}
        <KsIcon :icon="faAngle" />
        {{ Math.round(lastActiveA) }}&deg;
      </template>
    </div>
  </div>
</template>
